import axios from "@/config/axios"

const initiateState = () => {
  return {
    allInsurances: [],
    addInsurance: false,
    insuranceById: null,
    updateInsurance: false,
    updateInsuranceStatus: false,
    insuranceReport: false,
    removeInsurance: false,
    commercialInsuranceSettings: null,
    updateMedicalGroupStatus: false,
    commercialFacilitiesByLabId: [],
    insuranceHistory: null,
  }
}
const state = initiateState()
const getters = {
  getInsurances: (state) => state.allInsurances,
  getInsuranceCreationStatus: (state) => state.addInsurance,
  getInsuranceDetails: (state) => state.insuranceById,
  getUpdateInsuranceStatus: (state) => state.updateInsurance,
  getUpdateInsuranceStatusResult: (state) => state.updateInsuranceStatus,
  getInsuranceReport: (state) => state.insuranceReport,
  getInsuranceRemoveStatus: (state) => state.removeInsurance,
  getCommercialInsuranceSettings: (state) => state.commercialInsuranceSettings,
  getUpdateMedicalGroupStatus: (state) => state.updateMedicalGroupStatus,
  getCommercialFacilitiesByLabId: (state) => state.commercialFacilitiesByLabId,
  getInsuranceHistory: (state) => state.insuranceHistory,
}
const mutations = {
  setInsurances(state, data) {
    state.allInsurances = data
  },
  setAddInsurance(state, data) {
    state.addInsurance = data
  },
  setInsuranceDetails(state, data) {
    state.insuranceById = data
  },
  setUpdateInsurance(state, data) {
    state.updateInsurance = data
  },
  setUpdateInsuranceStatus(state, data) {
    state.updateInsuranceStatus = data
  },
  setInsuranceReport(state, data) {
    state.insuranceReport = data
  },
  setInsuranceStatus(state, data) {
    state.removeInsurance = data
  },
  setCommercialInsuranceSettings(state, data) {
    state.commercialInsuranceSettings = data
  },
  setUpdateMedicalGroupStatus(state, data) {
    state.updateMedicalGroupStatus = data
  },
  setCommercialFacilitiesByLabId(state, data) {
    state.commercialFacilitiesByLabId = data
  },
  setInsuranceHistory(state, data) {
    state.insuranceHistory = data
  },
  reset: (state) => {
    const initial = initiateState()
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key]
    })
  },
}
const actions = {
  fetchAllInsurances: async ({ commit, dispatch }, params) => {
    try {
      await dispatch("errors/clear", [], { root: true })

      const response = await axios.get("commercial-insurance-rules", { params })

      commit("setInsurances", response.data)
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  createInsurance: async ({ commit, dispatch }, params) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setAddInsurance", false)
      await axios.post("commercial-insurance-rules", params)

      commit("setAddInsurance", true)
    } catch (error) {
      commit("setAddInsurance", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  getInsuranceById: async ({ commit, dispatch }, params) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      let insurance = params.insurance_id
      const response = await axios.get(
        `commercial-insurance-rules/${insurance}`,
        { params },
      )

      commit("setInsuranceDetails", response.data)
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  updateInsurance: async (
    { commit, dispatch },
    { insuranceData, insurance },
  ) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setUpdateInsurance", false)
      await axios.patch(
        `commercial-insurance-rules/${insurance}`,
        insuranceData,
      )

      commit("setUpdateInsurance", true)
    } catch (error) {
      commit("setUpdateInsurance", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  exportInsurances: async ({ commit, dispatch }, params) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setInsuranceReport", false)
      let response = await axios.get("insurances/download-as-csv", { params })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "Insurances.csv")
      document.body.appendChild(link)
      link.click()
      commit("setInsuranceReport", true)
    } catch (error) {
      commit("setInsuranceReport", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  updateInsuranceStatus: async ({ commit, dispatch }, { insurance_id, status }) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setUpdateInsuranceStatus", false)
      await axios.patch(`commercial-insurance-rules/${insurance_id}/status`, { status })
      commit("setUpdateInsuranceStatus", true)
    } catch (error) {
      commit("setUpdateInsuranceStatus", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true })
      }
    }
  },
  removeInsuranceById: async ({ commit, dispatch }, params) => {
    try {
      let insurance = params.insurance_id
      delete params.insurance_id
      await dispatch("errors/clear", [], { root: true })
      commit("setInsuranceStatus", false)
      await axios.delete(`/insurance-payors/${insurance}`)

      commit("setInsuranceStatus", true)
    } catch (error) {
      commit("setInsuranceStatus", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  fetchInsuranceHistory: async ({ commit, dispatch }, params) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      const insurance = params.insurance_id
      const queryParams = { ...params }
      delete queryParams.insurance_id
      const response = await axios.get(
        `commercial-insurance-rules/${insurance}/audit-logs`,
        { params: queryParams },
      )

      commit("setInsuranceHistory", response.data)
    } catch (error) {
      commit("setInsuranceHistory", null)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  fetchCommercialInsuranceSettings: async ({ commit, dispatch }) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      const response = await axios.get("users/get-bot")

      commit("setCommercialInsuranceSettings", response.data.data)
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  updateCommercialInsuranceSettings: async ({ commit, dispatch }) => {
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setUpdateMedicalGroupStatus", false)
      await axios.patch("users/get-bot/toggle-status")
      commit("setUpdateMedicalGroupStatus", true)
    } catch (error) {
      commit("setUpdateMedicalGroupStatus", false)
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
  fetchCommercialFacilitiesByLabId: async ({ commit, dispatch }, labid) => {
    console.log('labid: ', labid);
    try {
      await dispatch("errors/clear", [], { root: true })
      commit("setCommercialFacilitiesByLabId", [])
      const response = await axios.get(
        `/commercial-facility-rules/${labid.labid}`,
      )
      commit("setCommercialFacilitiesByLabId", response.data.data)
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, {
          root: true,
        })
      }
    }
  },
}
export default {
  namespaced: true,
  mutations,
  getters,
  state,
  actions,
}
